/* globals getBasedOnEnvironment:true */
import type { Client, UserAttributes } from '@optimizely/optimizely-sdk';
import optimizelySdk from '@optimizely/optimizely-sdk';
import { log } from '@hotjar/insights-common/lib/log';

import { getVariantFromUrl } from './overrides';
import { VARIANTS } from './constants';

const OPTIMIZELY_PROD_KEY = 'HgHVKrf9ZD2dsZYVFb9JnD';
const OPTIMIZELY_STAGING_KEY = 'KuEt7q1JBDm2SmK6QirC64';
const OPTIMIZELY_DEV_KEY = 'W57LsCZfHqi4eeyR2rLGrv';

const SDK_KEY = getBasedOnEnvironment({
  prod: OPTIMIZELY_PROD_KEY,
  staging: OPTIMIZELY_STAGING_KEY,
  dev: OPTIMIZELY_DEV_KEY,
});
const LOG_LEVEL_WARNING = 3;
const DEFAULT_VARIANT = VARIANTS.control;

let cachedClientPromise: Promise<Client> | undefined;

function isTestEnv() {
  return getBasedOnEnvironment({ test: true, default: false });
}

function initOptimizelyClient(): Promise<Client> {
  let client: Client;

  try {
    client = optimizelySdk.createInstance({
      sdkKey: SDK_KEY,
      logLevel: LOG_LEVEL_WARNING,
    });
  } catch (error) {
    // FIXME prefer-promise-reject-errors
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ error });
  }

  if (client === undefined) {
    // FIXME prefer-promise-reject-errors
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ error: 'Optimizely client failed to instantiate' });
  }

  return client.onReady().then(({ success }) => {
    if (!success) {
      // FIXME prefer-promise-reject-errors
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ error: 'Optimizely timed out waiting for client instance' });
    }
    return client;
  });
}

function getClient() {
  cachedClientPromise = cachedClientPromise || initOptimizelyClient();
  return cachedClientPromise;
}

function getVariant(
  experimentId: string,
  targetId: string,
  attributes?: UserAttributes,
): Promise<VARIANTS | null> {
  if (isTestEnv()) {
    log.debug('Optimizely: skipping since we are in the test environment');
    return Promise.resolve(DEFAULT_VARIANT);
  }

  const variantInUrl = getVariantFromUrl(experimentId);

  if (variantInUrl) {
    return Promise.resolve(variantInUrl);
  }

  return getClient().then(
    (optimizelyClientInstance) =>
      optimizelyClientInstance.getVariation(experimentId, targetId, attributes) as VARIANTS | null,
  );
}

function activate(
  experimentId: string,
  targetId: string,
  attributes?: UserAttributes,
): Promise<VARIANTS | null> {
  const variantInUrl = getVariantFromUrl(experimentId);

  if (variantInUrl) {
    return Promise.resolve(variantInUrl);
  }

  if (isTestEnv()) {
    log.debug(
      `Optimizely: skipping since we are in the test environment.
        If you want to test different variants, set appropriate variant in the url.`,
    );
    return Promise.resolve(DEFAULT_VARIANT);
  }

  return getClient().then(
    (optimizelyClientInstance) =>
      optimizelyClientInstance.activate(experimentId, targetId, attributes) as VARIANTS | null,
  );
}

function isFeatureEnabled(
  featureId: string,
  targetId: string,
  attributes?: UserAttributes,
): Promise<boolean> {
  if (isTestEnv()) {
    log.debug('Optimizely: skipping since we are in the test environment');
    return Promise.resolve(false);
  }

  return getClient().then((optimizelyClientInstance) =>
    optimizelyClientInstance.isFeatureEnabled(featureId, targetId, attributes),
  );
}

function reset(): void {
  cachedClientPromise = undefined;
}

export const optimizely = { reset, getVariant, activate, isFeatureEnabled };
